import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, HardDrive, ShieldCheck, Trash2 } from 'lucide-react';
import { formatBytes } from '@/lib/format';
import {
  readStorageEstimate,
  removeAllVideos,
  requestPersistence,
  usageByCategory,
  type CategoryUsage,
  type StorageEstimate,
} from '@/services/storage/StorageQuotaService';

/** Tela de armazenamento offline (F13-T10 a T13). */
export function StorageMeter(): React.ReactElement {
  const [estimate, setEstimate] = useState<StorageEstimate | null>(null);
  const [byCategory, setByCategory] = useState<CategoryUsage[]>([]);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [next, categories] = await Promise.all([readStorageEstimate(), usageByCategory()]);
    setEstimate(next);
    setByCategory(categories);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const askPersistence = async (): Promise<void> => {
    setBusy(true);
    await requestPersistence();
    await refresh();
    setBusy(false);
  };

  const removeAll = async (): Promise<void> => {
    setBusy(true);
    await removeAllVideos();
    await refresh();
    setConfirmingRemoval(false);
    setBusy(false);
  };

  if (!estimate) {
    return <div className="h-40 animate-pulse rounded-xl bg-slate-800" aria-hidden />;
  }

  const apiUnavailable = estimate.quotaBytes === null;

  return (
    <section className="rounded-xl bg-slate-800 p-4">
      <h2 className="flex items-center gap-2 font-medium">
        <HardDrive size={18} aria-hidden />
        Armazenamento offline
      </h2>

      <dl className="mt-3 space-y-1 text-sm">
        <Row label="Vídeos" value={formatBytes(estimate.mediaBytes)} />
        <Row label="Dados do app" value={formatBytes(estimate.appBytes)} />
        <Row label="Disponível" value={estimate.availableBytes === null ? 'desconhecido' : `~${formatBytes(estimate.availableBytes)}`} />
      </dl>

      {estimate.quotaBytes !== null && estimate.usageBytes !== null && (
        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-slate-700"
          role="progressbar"
          aria-label="Uso do armazenamento"
          aria-valuenow={Math.round((estimate.usageBytes / estimate.quotaBytes) * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-sky-400"
            style={{ width: `${Math.min(100, (estimate.usageBytes / estimate.quotaBytes) * 100)}%` }}
          />
        </div>
      )}

      <p className="mt-3 text-sm text-slate-400">
        {estimate.videosStored} {estimate.videosStored === 1 ? 'vídeo' : 'vídeos'} no dispositivo
      </p>

      {apiUnavailable && (
        <p className="mt-3 rounded-lg bg-slate-700/60 p-3 text-xs text-slate-300">
          Este navegador não informa o espaço disponível. Os downloads continuam
          funcionando, mas não dá para prever quando o espaço vai acabar.
        </p>
      )}

      {/* Persistência: sem ela o sistema pode apagar tudo (F13-T06, F13-T09). */}
      <div className="mt-4 border-t border-slate-700 pt-4">
        {estimate.persisted === true && (
          <p className="flex items-center gap-2 text-sm text-emerald-300">
            <ShieldCheck size={16} aria-hidden />
            Armazenamento persistente concedido.
          </p>
        )}

        {estimate.persisted === false && (
          <div className="flex flex-col gap-2">
            <p className="flex items-start gap-2 text-sm text-amber-300">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <span>
                O sistema pode apagar os vídeos baixados se o dispositivo ficar sem espaço.
                Peça persistência antes de viajar.
              </span>
            </p>
            <button
              type="button"
              onClick={() => void askPersistence()}
              disabled={busy}
              className="min-h-11 rounded-lg bg-slate-700 px-4 text-sm font-medium active:bg-slate-600 disabled:opacity-60"
            >
              Solicitar armazenamento persistente
            </button>
          </div>
        )}

        {estimate.persisted === null && (
          <p className="text-xs text-slate-400">
            Este navegador não permite solicitar armazenamento persistente.
          </p>
        )}
      </div>

      {byCategory.length > 0 && (
        <div className="mt-4 border-t border-slate-700 pt-4">
          <h3 className="text-sm font-medium text-slate-300">Por categoria</h3>
          <ul className="mt-2 space-y-1 text-sm">
            {byCategory.map((usage) => (
              <li key={usage.categoryId} className="flex justify-between gap-3">
                <span className="truncate text-slate-400">{usage.categoryName}</span>
                <span className="shrink-0 tabular-nums text-slate-300">
                  {usage.videos} · {formatBytes(usage.bytes)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {estimate.videosStored > 0 && (
        <div className="mt-4 border-t border-slate-700 pt-4">
          {confirmingRemoval ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-300">
                Remover os {estimate.videosStored} vídeos baixados? Seus favoritos, progresso
                e histórico são mantidos.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void removeAll()}
                  disabled={busy}
                  className="min-h-11 flex-1 rounded-lg bg-red-500/20 px-4 text-sm font-medium text-red-200 active:bg-red-500/30 disabled:opacity-60"
                >
                  Remover tudo
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemoval(false)}
                  className="min-h-11 flex-1 rounded-lg bg-slate-700 px-4 text-sm active:bg-slate-600"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingRemoval(true)}
              className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-slate-400 active:bg-slate-700"
            >
              <Trash2 size={16} aria-hidden />
              Remover todos os vídeos
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className="tabular-nums text-slate-200">{value}</dd>
    </div>
  );
}
